/**
 * Règlements d'encours de la période sur un Z (z_closures.encours_settlements) : état SÉPARÉ du chiffre d'affaires,
 * jamais additionné aux encaissements de ventes. NULL sur les Z antérieurs : rien n'est affiché.
 * Source unique du texte pour les trois chemins d'impression et l'export (miroir 4.A.16).
 */
import { z } from 'zod';

const ScopeSchema = z.object({
  count: z.coerce.number(),
  total: z.coerce.number(),
  by_method: z.record(z.coerce.number()),
});

const SettlementsSchema = z.object({
  register: ScopeSchema,
  external: ScopeSchema,
  reversals: z.object({ count: z.coerce.number(), total: z.coerce.number() }),
});

export type ZEncoursSettlements = z.infer<typeof SettlementsSchema>;

/** État lisible et non vide, sinon null (Z ancien, aucun règlement sur la période). */
export const parseZEncours = (value: unknown): ZEncoursSettlements | null => {
  const parsed = SettlementsSchema.safeParse(value);
  if (!parsed.success) return null;
  const { register, external, reversals } = parsed.data;
  return register.count + external.count + reversals.count > 0 ? parsed.data : null;
};

const row = (method: string, amount: number, width: number): string =>
  '  ' + method.padEnd(width) + amount.toFixed(2).padStart(10) + ' E';

export const zEncoursLines = (value: unknown, width: number): string[] => {
  const state = parseZEncours(value);
  if (!state) return [];
  const lines: string[] = ['Encours regles (hors CA):'];
  if (state.register.count > 0) {
    lines.push('En caisse: ' + state.register.total.toFixed(2) + ' EUR');
    for (const [m, amt] of Object.entries(state.register.by_method)) lines.push(row(m, amt, width));
  }
  if (state.external.count > 0) {
    lines.push('Hors caisse (Lekompte): ' + state.external.total.toFixed(2) + ' EUR');
    for (const [m, amt] of Object.entries(state.external.by_method)) lines.push(row(m, amt, width));
  }
  if (state.reversals.count > 0) {
    lines.push('Reglements annules: -' + state.reversals.total.toFixed(2) + ' EUR');
  }
  return lines;
};
